import React, { useEffect, useState, useMemo } from 'react';
import { round } from 'lodash';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { getOrientation } from 'utils/orientation';
import { buildImageNodeCropStyles, buildImageNodeStyles } from 'utils/node_util';
import { invertCroppedDimension } from 'utils/math_util';
import { renderModal } from 'utils/modal_util';

import Button from 'components/shared/Button';
import Modal from 'components/shared/Modal';
import ImageNode from 'components/shared/Nodes/ImageNode';

import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';

import crops from 'domains/crops';
import ui from 'domains/ui';
import cartProductCreator from 'domains/cart_product_creator';
import multiphotos from 'domains/multiphotos';

import { useCalculatedBounds } from './hooks';

import type { NodeProps } from './Node';

function ImageNodeContainer(props: NodeProps) {
  const {
    node,
    canvasId,
    canvasRotation,
    canvasRotationAllowed,
    canvasOrientation,
    isMultiNodeProduct,
  } = props;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const cropId = useMemo(() => crops.utils.buildCropId(canvasId, node.uid), [canvasId, node.uid]);

  const dispatch = useAppDispatch();
  const activeCanvasId = useAppSelector(activeEditor.selectors.canvasId);
  const activeImageNode = useAppSelector(crops.selectors.activeImageNode);
  const multiphotoIds = useAppSelector(multiphotos.selectors.selectIds);
  const potentialCropIds = useAppSelector(crops.selectors.potentialCropIds);
  const hasMobileNavigation = useAppSelector(ui.selectors.hasMobileNavigation);
  const addToCartQuantity = useAppSelector(cartProductCreator.selectors.quantity);

  const thisNodeCropData = useAppSelector((state) => crops.selectors.selectById(state, cropId));
  const nodePhoto = useAppSelector((state) => crops.selectors.photoByCropId(state, cropId));
  const imageSrc = useAppSelector((state) => crops.selectors.cropBlobSrc(state, cropId));
  const cropLoading = useAppSelector((state) => crops.selectors.photoLoading(state, cropId));

  const [x, y, w, h] = useCalculatedBounds(canvasId, node.uid);

  const nodeDimension = useMemo(
    () => ({
      height: canvasRotation.rotation === 0 ? h : w,
      width: canvasRotation.rotation === 0 ? w : h,
    }),
    [w, h, canvasRotation.rotation]
  );

  const nodePoint = useMemo(
    () => ({
      x: canvasRotation.rotation === 0 ? x : y,
      y: canvasRotation.rotation === 0 ? y : x,
    }),
    [x, y, canvasRotation.rotation]
  );

  const scaledPhotoWidth = useMemo(() => {
    if (!thisNodeCropData || !thisNodeCropData.cropW) return null;
    const scaled = invertCroppedDimension(nodeDimension.width, thisNodeCropData.cropW);
    if (scaled === 0) return null;

    return scaled;
  }, [thisNodeCropData, nodeDimension]);

  const scaledPhotoHeight = useMemo(() => {
    if (!thisNodeCropData || !thisNodeCropData.cropH) return null;
    const scaled = invertCroppedDimension(nodeDimension.height, thisNodeCropData.cropH);
    if (scaled === 0) return null;

    return scaled;
  }, [thisNodeCropData, nodeDimension]);

  const [isDropTarget, setIsDropTarget] = useState(false);

  const usingRotatedImage = useMemo(
    () => !!thisNodeCropData && thisNodeCropData.rotation !== 0,
    [thisNodeCropData]
  );

  const cropNodeAspectRatio = useMemo(() => {
    return thisNodeCropData?.nodeAspectRatio;
  }, [thisNodeCropData]);

  // We need to update crops if node dimension aspect ratio changes
  // This indicates a rotated canvas or a new product, which means we need
  // to re-fit the image to the node
  useEffect(() => {
    if (nodeDimension.width && nodeDimension.height && cropNodeAspectRatio) {
      const updateAspectRatio = crops.utils.roundAspectRatio(nodeDimension);
      if (cropNodeAspectRatio !== updateAspectRatio) {
        dispatch(
          crops.actions.updateCropNodeDimensions({
            nodeDimension,
            canvasId,
            nodeUid: node.uid,
          })
        );
      }
    }
  }, [dispatch, canvasId, node.uid, nodeDimension, cropNodeAspectRatio]);

  // Style for entire node
  const styles = useMemo(
    () => buildImageNodeStyles(node, nodeDimension, nodePoint, isDropTarget),
    [node, nodeDimension, nodePoint, isDropTarget]
  );

  // Apply crop data
  const imageStyle = useMemo(() => {
    if (!thisNodeCropData) return {};

    return buildImageNodeCropStyles(thisNodeCropData, nodeDimension);
  }, [thisNodeCropData, nodeDimension]);

  // Is this the active image node
  const isActive = useMemo(() => {
    return activeCanvasId === canvasId && activeImageNode === node.uid;
  }, [activeImageNode, node.uid, activeCanvasId, canvasId]);

  const imgAlt = useMemo(() => {
    let alt = '';

    if (nodePhoto && nodePhoto.original_filename) {
      alt = nodePhoto.original_filename;
    }

    return alt;
  }, [nodePhoto]);

  // Set the height & width of the full image, triggers init of crop data
  // Auto-rotates if conditions are correct
  function handleImageLoaded(e: React.SyntheticEvent<HTMLImageElement>) {
    const image = e.currentTarget;
    const { naturalHeight, naturalWidth } = image;
    const imageOrientation = getOrientation(naturalWidth, naturalHeight);
    const canAutoRotate =
      canvasRotationAllowed && !canvasRotation.userRotated && !usingRotatedImage;

    const shouldAutoRotate =
      imageOrientation !== 'square' && imageOrientation !== canvasOrientation;

    // Init needs to happen before auto-rotation, since canvas rotation
    // re-establishes crop dimensions
    dispatch(
      crops.actions.initCropDimensions({
        canvasId,
        nodeUid: node.uid,
        nodeDimension,
        imageDimensions: { height: naturalHeight, width: naturalWidth },
      })
    );

    dispatch(crops.actions.setCropPhotoLoaded({ id: cropId, loaded: true }));

    if (canAutoRotate && shouldAutoRotate) {
      dispatch(canvases.actions.rotateCanvas(canvasId));
    }
  }

  function handleClearImage() {
    dispatch(crops.actions.deleteCrop(cropId));
  }

  function handlePanCrop(movementX: number, movementY: number) {
    if (!scaledPhotoWidth || !scaledPhotoHeight || (!movementX && !movementY)) return;

    dispatch(
      crops.actions.panCrop({
        canvasId,
        nodeUid: node.uid,
        movementX: round(movementX / scaledPhotoWidth, 6),
        movementY: round(movementY / scaledPhotoHeight, 6),
        flipTrackingDirection: Number(node.rotation) !== 0 && canvasRotation.rotation === 0,
      })
    );
  }

  // Show mobile user photos when empty image node is clicked
  function handleImageNodeClick() {
    if (hasMobileNavigation && imageSrc === '') {
      dispatch(ui.actions.setShowMobileUserPhotos(true));
    }

    dispatch(crops.actions.setActiveImageNode(node.uid));
    dispatch(canvases.actions.setActiveCanvasId(canvasId));
  }

  function handleImageNodeDragEnter(e: React.DragEvent) {
    e.preventDefault();
    setIsDropTarget(true);
  }

  function addProductsForMultipleSelect() {
    dispatch(cartProductCreator.actions.addToCart.pending('multiphoto'));
    setShowConfirmDialog(false);
  }

  const multiPhotoAlertButtons = (
    <React.Fragment>
      <Button type="confirm" onClick={addProductsForMultipleSelect}>
        Add Multiple Items to Cart
      </Button>
      <Button type="cancel" onClick={() => setShowConfirmDialog(false)}>
        Do Not Add
      </Button>
    </React.Fragment>
  );

  function handleImageNodeDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDropTarget(false);
    const dragPhoto = parseInt(e.dataTransfer.getData('text/plain'));
    if (multiphotoIds.length > 1 && potentialCropIds.length === 1) {
      setShowConfirmDialog(true);
    } else if (dragPhoto) {
      dispatch(crops.actions.createCrop({ canvasId, nodeUid: node.uid, photoId: dragPhoto }));
      dispatch(crops.actions.setNextActiveImageNode());
    }
  }

  return (
    <React.Fragment>
      <ImageNode
        uid={node.uid}
        imageSrc={imageSrc}
        cropLoading={cropLoading}
        styles={styles}
        imageStyle={imageStyle}
        imgAlt={imgAlt}
        isActive={isActive}
        isMultiNodeProduct={isMultiNodeProduct}
        onLoad={handleImageLoaded}
        onClear={handleClearImage}
        onClick={handleImageNodeClick}
        onDragEnter={handleImageNodeDragEnter}
        onDragLeave={() => setIsDropTarget(false)}
        onDrop={handleImageNodeDrop}
        onPan={handlePanCrop}
      />

      {showConfirmDialog &&
        renderModal(
          <Modal
            title="Confirm Multiple Add to Cart"
            labelledBy="confirm-multi-photo-modal"
            lastFocusedElementId="first-photo-thumb"
            onClose={() => setShowConfirmDialog(false)}
            primaryFooterGroup={multiPhotoAlertButtons}
          >
            <div className="alert-message">
              <div>
                You have <strong>{multiphotoIds.length}</strong> photos selected.
              </div>
              <div>
                Would you like to add {addToCartQuantity} of this item for each selected photo?
              </div>
            </div>
          </Modal>
        )}
    </React.Fragment>
  );
}

export default ImageNodeContainer;
